/**
 * CR-5880: the inline add / edit row of the KPI Milestones table.
 */
import { useState } from 'react';
import type { KpiMilestoneInput, MilestoneAppliesTo } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { TableCell, TableRow } from '../../ui/table';
import { Check, Loader2, X } from 'lucide-react';

export const APPLIES_TO_LABELS: Record<MilestoneAppliesTo, string> = {
  otif: 'OTIF',
  otd: 'OTD',
  both: 'OTIF & OTD',
};

interface MilestoneFormRowProps {
  initial?: KpiMilestoneInput;
  saving: boolean;
  /** @returns whether it was saved (the row closes only then) */
  onSave: (data: KpiMilestoneInput) => Promise<boolean>;
  onCancel: () => void;
}

const EMPTY: KpiMilestoneInput = { title: '', milestone_date: '', applies_to: 'both', is_active: true };

export function MilestoneFormRow({ initial, saving, onSave, onCancel }: MilestoneFormRowProps) {
  const [form, setForm] = useState<KpiMilestoneInput>(initial ?? EMPTY);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!form.title.trim()) return setError('Enter a milestone name.');
    if (!form.milestone_date) return setError('Pick the milestone date.');
    setError(null);
    await onSave({ ...form, title: form.title.trim() });
  };

  return (
    <TableRow className="bg-muted/30">
      <TableCell>
        <Input
          aria-label="Milestone name"
          placeholder="e.g. ERP go-live"
          value={form.title}
          maxLength={200}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        {error && <p className="text-xs text-destructive mt-1" role="alert">{error}</p>}
      </TableCell>
      <TableCell>
        <Input
          type="date"
          aria-label="Milestone date"
          value={form.milestone_date}
          onChange={(e) => setForm({ ...form, milestone_date: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <select
          aria-label="Applies to"
          value={form.applies_to}
          onChange={(e) => setForm({ ...form, applies_to: e.target.value as MilestoneAppliesTo })}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {(Object.keys(APPLIES_TO_LABELS) as MilestoneAppliesTo[]).map((key) => (
            <option key={key} value={key}>{APPLIES_TO_LABELS[key]}</option>
          ))}
        </select>
      </TableCell>
      <TableCell>
        <Switch
          aria-label="Active"
          checked={form.is_active}
          onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
        />
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button size="sm" onClick={submit} disabled={saving} aria-label="Save milestone">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          <span className="ml-1">Save</span>
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} aria-label="Cancel">
          <X className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
